/**
 * Validate Session Modal
 *
 * A resumable, step-by-step popup for validating a session's fields. Each screen shows
 * the field's transcript/notes evidence (reused FieldEvidenceView, same as clicking a
 * field) on the left and the value editor/validation on the right. The reviewer validates
 * the core fields first, then a random top-up of non-core fields, until the backend
 * marks the session "validated" (all core + >= 12 total reviewed).
 *
 * Each "Next" saves the current step's values via PUT /api/sessions/{id}/validation, which
 * locks them into human_overrides under a USER_VALIDATION source and advances validation_state —
 * so progress persists and the popup resumes where the reviewer left off (fully-validated
 * steps are skipped on open). Field editing reuses EditFieldRenderer + resolveFieldOptions,
 * and principal+detailed body part / primary+secondary object are grouped on one screen
 * since one change can affect the other.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Button from '@amzn/meridian/button';
import RadioButton from '@amzn/meridian/radio-button';
import Textarea from '@amzn/meridian/textarea';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { Session, isValidationEdit, validateSessionFields } from '../../../store/sessionSlice';
import { showError, showSuccess } from '../../../store/toastsSlice';
import { sessionService } from '../../../services/sessionService';
import { METRIC_KEYS } from '../../../constants';
import {
  FieldValue,
  fieldValuesEqual,
  formatFieldValueOneLine,
  getFieldDefinition,
  getFieldLabel,
  getFieldQuestion,
  parentValueOf,
  getQuestionFields,
  isFieldPending,
  isGroupedFieldValue,
  mergeWithHumanOverrides,
  toParentValues,
} from '../../../utils/fieldUtils';
import FieldDropdown from '../../shared/session/FieldDropdown';
import StepReviewModal from './StepReviewModal';
import { FONT_SIZE_BODY, FONT_SIZE_SMALL, TEXT_COLOR_PRIMARY } from '../../shared/cssConstants';

interface ValidationStep {
  fields: string[];       // fields edited on this screen (grouped where related)
  evidenceField: string;  // field whose evidence is shown on the left
  commentField: string;   // group's core field — where the comment/accurate state lives
}

// Core fields the reviewer must validation, grouped into screens. Body-part and object
// fields are grouped because a parent value changes the child's options. The body-part
// step also carries detailed_body_part_symptoms so that adding a new body part surfaces
// its (empty) symptom select for the reviewer to fill.
// Field names follow the initial_information_and_encounter (WHS) profile.
const CORE_STEPS: ValidationStep[] = [
  { fields: ['location_detail'], evidenceField: 'location_detail', commentField: 'location_detail' },
  {
    fields: ['principal_body_part', 'detailed_body_part', 'detailed_body_part_symptoms'],
    evidenceField: 'detailed_body_part',
    commentField: 'detailed_body_part',
  },
  {
    fields: ['type_of_impact_primary', 'type_of_impact_secondary'],
    evidenceField: 'type_of_impact_primary',
    commentField: 'type_of_impact_primary',
  },
  { fields: ['primary_object', 'secondary_object'], evidenceField: 'primary_object', commentField: 'primary_object' },
  { fields: ['risk_hazard'], evidenceField: 'risk_hazard', commentField: 'risk_hazard' },
  { fields: ['process_path'], evidenceField: 'process_path', commentField: 'process_path' },
];
const CORE_FIELDS = new Set(([] as string[]).concat(...CORE_STEPS.map((s) => s.fields)));
// Core fields that must have a collected value before a session can be validated
// (principal_body_part is a grouping companion, not required on its own).
const CORE_REQUIRED_FIELDS = [
  'location_detail',
  'type_of_impact_primary',
  'detailed_body_part',
  'primary_object',
  'secondary_object',
  'risk_hazard',
  'process_path',
];
// Non-core fields the reviewer must additionally validation (topped up to this many).
const RANDOM_TARGET = 5;

interface ValidateSessionModalProps {
  open: boolean;
  session: Session;
  onClose: () => void;
}

function shuffle<T>(arr: T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** Validation unit for one step (single field, or a parent+child group). Shows one
 *  accurate/not-accurate choice; when inaccurate, a labeled dropdown per field and a
 *  single comment. For a group the title lists both field names and only the parent's
 *  question is shown. */
const ValidationStepEditor: React.FC<{
  fields: string[];
  companionFields: string[];
  values: Record<string, FieldValue>;
  onChange: (fieldName: string, value: FieldValue) => void;
  showComment: boolean;
  preChanged: boolean;
  editing: boolean;
  onEditToggle: () => void;
  isAccurate: boolean;
  onAccurateChange: (accurate: boolean) => void;
  comment: string;
  commentRequired: boolean;
  onCommentChange: (comment: string) => void;
}> = ({
  fields,
  companionFields,
  values,
  onChange,
  showComment,
  preChanged,
  editing,
  onEditToggle,
  isAccurate,
  onAccurateChange,
  comment,
  commentRequired,
  onCommentChange,
}) => {
  const fieldsConfig = useAppSelector((state) => state.config.fieldsConfig);
  const isGroup = fields.length > 1;
  const title = fields.map((f) => getFieldLabel(fieldsConfig, f)).join(' & ');

  const editorFor = (f: string) => {
    const parentValue = parentValueOf(fieldsConfig, f, values);
    return (
      <FieldDropdown
        key={f}
        fieldName={f}
        value={values[f]}
        parentValue={parentValue}
        label={getFieldLabel(fieldsConfig, f)}
        onChange={onChange}
      />
    );
  };
  const editors = fields.map(editorFor);
  const companionEditors = companionFields.map(editorFor);

  const commentBlock = !showComment ? null : (
    <div style={{ marginTop: '12px' }}>
      <Textarea
        value={comment}
        onChange={(v: string) => onCommentChange(v)}
        label={commentRequired ? 'Comment (required)' : 'Comment (optional)'}
        placeholder="Explain why the value was changed"
        rows={2}
        size="small"
        error={commentRequired && !comment.trim()}
        errorMessage={commentRequired && !comment.trim() ? 'A comment is required.' : undefined}
      />
    </div>
  );

  return (
    <div style={{ marginBottom: '24px' }}>
      {isGroup && (
        <div style={{ fontSize: FONT_SIZE_BODY, fontWeight: 500, color: TEXT_COLOR_PRIMARY, marginBottom: '8px' }}>
          {title}
        </div>
      )}
      {preChanged ? (
        /* Already edited before validation: show current value(s) + one Edit toggle. */
        <div>
          <div style={{ fontSize: FONT_SIZE_SMALL, color: TEXT_COLOR_PRIMARY }}>Current Answer</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <span style={{ fontSize: FONT_SIZE_BODY, color: TEXT_COLOR_PRIMARY }}>
              {fields.map((f) => formatFieldValueOneLine(values[f])).join('  /  ')}
            </span>
            <Button type="link" size="small" onClick={onEditToggle}>
              {editing ? 'Done' : 'Edit'}
            </Button>
          </div>
          {editing && editors}
          {commentBlock}
        </div>
      ) : (
        /* Not previously changed: one accurate/not-accurate choice for the whole step. */
        <div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '8px' }}>
            <RadioButton checked={isAccurate} value="accurate" onChange={() => onAccurateChange(true)}>
              Value is accurate
            </RadioButton>
            <RadioButton checked={!isAccurate} value="not_accurate" onChange={() => onAccurateChange(false)}>
              Value is not accurate
            </RadioButton>
          </div>
          {!isAccurate ? (
            <div>
              {editors}
              {commentBlock}
            </div>
          ) : (
            /* Value confirmed accurate: still expose companion (e.g. secondary) fields
               so the reviewer can optionally add them without marking it inaccurate. */
            (companionEditors.length > 0 || showComment) && (
              <div>
                {companionEditors}
                {commentBlock}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

const ValidateSessionModal: React.FC<ValidateSessionModalProps> = ({ open, session, onClose }) => {
  const dispatch = useAppDispatch();
  const fieldsConfig = useAppSelector((state) => state.config.fieldsConfig);
  const [steps, setSteps] = useState<ValidationStep[]>([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [editedValues, setEditedValues] = useState<Record<string, FieldValue>>({});
  const [comments, setComments] = useState<Record<string, string>>({});
  const [accurate, setAccurate] = useState<Record<string, boolean>>({});
  const [editing, setEditing] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  // When the current step's screen was shown — used to publish validation dwell latency.
  const stepShownAtRef = useRef<number>(Date.now());
  useEffect(() => {
    stepShownAtRef.current = Date.now();
  }, [stepIndex, open]);

  const extracted = (session.extracted_fields || {}) as Record<string, FieldValue>;
  const overrides = (session.human_overrides_fields || {}) as Record<string, FieldValue>;
  // A field the user already changed before validation (override present and != extraction).
  const isPreChanged = (fieldName: string) =>
    overrides[fieldName] !== undefined && !fieldValuesEqual(overrides[fieldName], extracted[fieldName]);
  // Group-level state (accurate / comment) lives under the step's designated core field.
  const stepKeyOf = (step: ValidationStep) => step.commentField;
  const isStepPreChanged = (step: ValidationStep) => step.fields.some(isPreChanged);
  // Not accurate = a field was changed before validation, or the reviewer marked the step inaccurate.
  const isStepNotAccurate = (step: ValidationStep) =>
    isStepPreChanged(step) || accurate[stepKeyOf(step)] === false;
  // Comment is required (and recorded on the step's core field) only when that core
  // field's value differs from the extraction. Other group fields (e.g. secondary impact,
  // principal body part, symptoms) can change with just an optional comment.
  const isStepCommentRequired = (step: ValidationStep) => {
    const key = stepKeyOf(step);
    return (
      CORE_REQUIRED_FIELDS.includes(key) &&
      isStepNotAccurate(step) &&
      !fieldValuesEqual(editedValues[key], extracted[key])
    );
  };

  // True when a field still needs a value: pending outright, or (for a hierarchical
  // field) missing a child selection for one of its parent's values.
  const fieldNeedsInput = (f: string, values: Record<string, FieldValue>): boolean => {
    if (!fieldsConfig) return false;
    const def = getFieldDefinition(fieldsConfig, f);
    const val = values[f];
    if (def?.depends_on) {
      const parentVals = toParentValues(values[def.depends_on]);
      if (parentVals && parentVals.length > 0) {
        const grouped = isGroupedFieldValue(val) ? val : {};
        return parentVals.some((p) => {
          const raw = grouped[p];
          const arr = Array.isArray(raw) ? raw : raw ? [raw] : [];
          return arr.filter((x) => x !== '<UNKNOWN>').length === 0;
        });
      }
    }
    return isFieldPending(val);
  };

  // Grouped/hierarchical fields render one dropdown per parent value (e.g. a symptom
  // select per detailed body part). Adding a new parent leaves its child dropdown empty,
  // so on save flag any parent whose child selection is still empty/unknown.
  const unfilledGroupSelections = (step: ValidationStep): string[] => {
    if (!fieldsConfig) return [];
    const out: string[] = [];
    for (const f of step.fields) {
      const def = getFieldDefinition(fieldsConfig, f);
      if (!def?.depends_on) continue;
      const parentVals = toParentValues(editedValues[def.depends_on]);
      if (!parentVals || parentVals.length === 0) continue;
      const val = editedValues[f];
      const grouped = isGroupedFieldValue(val) ? val : {};
      for (const p of parentVals) {
        const raw = grouped[p];
        const arr = Array.isArray(raw) ? raw : raw ? [raw] : [];
        if (arr.filter((x) => x !== '<UNKNOWN>').length === 0) {
          out.push(`${getFieldLabel(fieldsConfig, f)} for ${p}`);
        }
      }
    }
    return out;
  };

  const handleAccurateChange = (key: string, value: boolean) => {
    setAccurate((prev) => ({ ...prev, [key]: value }));
  };

  const handleEditToggle = (key: string) => {
    setEditing((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Values the reviewer sees/edits (submitted > overrides > extracted).
  const merged = useMemo(
    () =>
      mergeWithHumanOverrides(
        session.extracted_fields || {},
        session.human_overrides_fields,
        session.submitted_fields,
      ) as Record<string, FieldValue>,
    [session.extracted_fields, session.human_overrides_fields, session.submitted_fields],
  );

  // Core fields still missing a collected value — the reviewer must gather these
  // (more recording / notes) before the session can be validated.
  const missingCore = useMemo(
    () =>
      fieldsConfig
        ? CORE_REQUIRED_FIELDS.filter(
            (f) => getFieldDefinition(fieldsConfig, f) && isFieldPending(merged[f]),
          )
        : [],
    [fieldsConfig, merged],
  );

  // Build the step list once per open: unvalidated core groups first, then a random
  // top-up of non-core fields to reach RANDOM_TARGET total non-core validated.
  useEffect(() => {
    if (!open || !fieldsConfig) return;

    // Missing core values block validating — render prompts to collect them instead.
    if (missingCore.length > 0) {
      setSteps([]);
      return;
    }

    const validatedFields = new Set(
      (session.edit_history || [])
        .filter(isValidationEdit)
        .map((e) => e.field_name),
    );

    // Include ALL core steps (validated or not) so the reviewer can navigate back to
    // already-validated fields; we just start at the first unvalidated step below.
    const nextSteps: ValidationStep[] = [];
    for (const step of CORE_STEPS) {
      const present = step.fields.filter((f) => getFieldDefinition(fieldsConfig, f));
      if (present.length === 0) continue;
      const evidenceField = present.includes(step.evidenceField) ? step.evidenceField : present[0];
      const commentField = present.includes(step.commentField) ? step.commentField : present[present.length - 1];
      nextSteps.push({ fields: present, evidenceField, commentField });
    }

    // Randoms: already-validated ones first (revisitable), then top up to RANDOM_TARGET.
    const randomPool = getQuestionFields(fieldsConfig).filter((f) => !CORE_FIELDS.has(f));
    const validatedRandoms = randomPool.filter((f) => validatedFields.has(f));
    const needed = Math.max(0, RANDOM_TARGET - validatedRandoms.length);
    // Only pick fields that actually have a value to review (skip empty/unknown).
    const candidates = randomPool.filter((f) => !validatedFields.has(f) && !isFieldPending(merged[f]));
    const newRandoms = shuffle(candidates).slice(0, needed);
    for (const f of validatedRandoms.concat(newRandoms)) nextSteps.push({ fields: [f], evidenceField: f, commentField: f });

    // Resume at the first step still holding an unvalidated field. If every field is
    // already validated, there's nothing to do — close so the header shows Report Incident.
    const firstUnvalidated = nextSteps.findIndex((s) => !s.fields.every((f) => validatedFields.has(f)));
    if (firstUnvalidated === -1) {
      onClose();
      return;
    }

    setSteps(nextSteps);
    setStepIndex(firstUnvalidated);
    // Seed editable values from current merged values for every field in the flow.
    const seed: Record<string, FieldValue> = {};
    for (const f of ([] as string[]).concat(...nextSteps.map((s) => s.fields))) {
      if (merged[f] !== undefined) seed[f] = merged[f];
    }
    setEditedValues(seed);
    // Seed comments from any prior validation entries so resuming shows what was written.
    const commentSeed: Record<string, string> = {};
    for (const e of session.edit_history || []) {
      if (isValidationEdit(e) && e.comment) commentSeed[e.field_name] = e.comment;
    }
    setComments(commentSeed);
    // Default each field to "accurate" unless the current value already differs from
    // the LLM extraction (e.g. a prior human override) — then start as not-accurate.
    const accurateSeed: Record<string, boolean> = {};
    for (const f of ([] as string[]).concat(...nextSteps.map((s) => s.fields))) {
      accurateSeed[f] = fieldValuesEqual(merged[f], (extracted as Record<string, FieldValue>)[f]);
    }
    setAccurate(accurateSeed);
    setEditing({});
    // Rebuild when fieldsConfig lands too, not just on open: an early open (e.g. the
    // ?validation=1 deep-link) can fire before the config is loaded, which would drop steps
    // whose field definitions aren't resolvable yet (e.g. location_detail). fieldsConfig
    // is a stable Redux reference that flips null->loaded once, so this rebuilds exactly
    // once — it won't reshuffle randoms on every keystroke the way adding `session` would.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fieldsConfig]);

  const handleCommentChange = (fieldName: string, comment: string) => {
    setComments((prev) => ({ ...prev, [fieldName]: comment }));
  };

  const handleChange = (fieldName: string, value: FieldValue) => {
    setEditedValues((prev) => ({ ...prev, [fieldName]: value }));
  };

  const currentStep: ValidationStep | undefined = steps[stepIndex];
  const isLastStep = stepIndex >= steps.length - 1;
  // Progress against the full validation target (core + randoms), counted from the persisted
  // validation history so already-validated fields (from a prior session) are included and
  // the count updates live as each step is saved.
  const totalFields = CORE_REQUIRED_FIELDS.length + RANDOM_TARGET;
  const validatedFieldNames = new Set<string>();
  const validatedRandomFields = new Set<string>();
  for (const e of session.edit_history || []) {
    if (!isValidationEdit(e)) continue;
    validatedFieldNames.add(e.field_name);
    if (!CORE_FIELDS.has(e.field_name)) validatedRandomFields.add(e.field_name);
  }
  const validatedCoreCount = CORE_REQUIRED_FIELDS.filter((f) => validatedFieldNames.has(f)).length;
  const validatedFields = validatedCoreCount + Math.min(RANDOM_TARGET, validatedRandomFields.size);

  const handleNext = async () => {
    if (!currentStep) return;

    const stepKey = stepKeyOf(currentStep);
    const notAccurate = isStepNotAccurate(currentStep);

    // Revisiting an already-validated step with no edits (value or comment unchanged from
    // what's persisted): skip the validation API call and the latency metric — just advance.
    const isRevisit = currentStep.fields.every((f) => validatedFieldNames.has(f));
    const valueChanged = currentStep.fields.some(
      (f) => !fieldValuesEqual(editedValues[f] ?? merged[f], merged[f]),
    );
    const priorComment =
      (session.edit_history || []).find(
        (e) => isValidationEdit(e) && e.field_name === stepKey,
      )?.comment || '';
    const commentChanged = (comments[stepKey] || '').trim() !== priorComment.trim();
    if (isRevisit && !valueChanged && !commentChanged) {
      if (isLastStep) onClose();
      else setStepIndex((i) => i + 1);
      return;
    }

    // When correcting values, every dropdown in the group must be filled (e.g. a newly
    // added body part must have its symptoms selected).
    if (notAccurate) {
      const unfilled = unfilledGroupSelections(currentStep);
      if (unfilled.length > 0) {
        dispatch(showError(`Please fill in: ${unfilled.join(', ')}.`));
        return;
      }
    }

    // Require a comment when a core step's value differs from the extraction.
    if (isStepCommentRequired(currentStep) && !(comments[stepKey] || '').trim()) {
      dispatch(showError('Please add a comment explaining the change.'));
      return;
    }

    // Send every field on this step (even unchanged) so it's recorded as reviewed.
    // Accurate → confirm the LLM extraction; not accurate → the reviewer's correction.
    // The group's comment is recorded on the child field (stepKey).
    const payload: Record<string, FieldValue> = {};
    const commentPayload: Record<string, string> = {};
    for (const f of currentStep.fields) {
      // Send the reviewer's value when the step is inaccurate, or when they edited this
      // field (e.g. added a secondary value while the primary stayed accurate); otherwise
      // confirm the LLM extraction.
      const edited = editedValues[f];
      const changed = edited !== undefined && !fieldValuesEqual(edited, extracted[f]);
      payload[f] =
        notAccurate || changed
          ? edited ?? merged[f] ?? '<UNKNOWN>'
          : extracted[f] ?? edited ?? merged[f] ?? '<UNKNOWN>';
    }
    if (notAccurate) {
      const c = (comments[stepKey] || '').trim();
      if (c) commentPayload[stepKey] = c;
    }

    // Capture pure screen dwell (screen shown -> Save clicked) now, before the await,
    // so the validation API round-trip doesn't inflate it. The next step's timer is reset
    // by the stepShownAtRef effect once stepIndex advances.
    const validationEvent = {
      field_name: stepKey,
      latency_ms: Date.now() - stepShownAtRef.current,
      timestamp: new Date().toISOString(),
    };

    setSaving(true);
    try {
      await dispatch(
        validateSessionFields({ sessionId: session.session_id, fields: payload, comments: commentPayload }),
      ).unwrap();
      // Publish validation dwell latency for this step (best-effort telemetry; never blocks).
      sessionService
        .appendMetrics(session.session_id, { [METRIC_KEYS.VALIDATION_LATENCY]: [validationEvent] })
        .catch(() => undefined);
      if (isLastStep) {
        dispatch(showSuccess('Validation saved'));
        onClose();
      } else {
        setStepIndex((i) => i + 1);
      }
    } catch (error: any) {
      dispatch(showError(typeof error === 'string' ? error : 'Failed to save validation'));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  // Core info not collected yet — ask the reviewer to gather it before validating.
  if (missingCore.length > 0) {
    return (
      <Modal open={open} onClose={onClose} title="Validate Session" scrollContainer="modal" closeLabel="Close">
        <div style={{ minWidth: '420px', maxWidth: '560px' }}>
          <div style={{ fontSize: FONT_SIZE_BODY, color: TEXT_COLOR_PRIMARY, marginBottom: '12px' }}>
            This session can&apos;t be validated yet — the following required fields still need
            information. Please collect them (continue recording or add a note) first:
          </div>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            {missingCore.map((f) => (
              <li key={f} style={{ fontSize: FONT_SIZE_BODY, color: TEXT_COLOR_PRIMARY, marginBottom: '4px' }}>
                {getFieldQuestion(fieldsConfig, f) || getFieldLabel(fieldsConfig, f)}
              </li>
            ))}
          </ul>
        </div>
        <ModalFooter>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="primary" size="small" onClick={onClose}>
              Got it
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    );
  }

  // Render nothing until the step list is built (or if there's nothing to validate,
  // in which case the build effect closes the modal so Report Incident can show).
  if (steps.length === 0) return null;

  return (
    <StepReviewModal
      open={open}
      onClose={onClose}
      evidenceFields={currentStep ? currentStep.fields : []}
      heading="Validation"
      progress={`Fields Validated: ${validatedFields} of ${totalFields}`}
      onPrevious={() => setStepIndex((i) => Math.max(0, i - 1))}
      onNext={handleNext}
      nextLabel={isLastStep ? 'Save & Finish' : 'Save & Next →'}
      busy={saving}
      canGoBack={stepIndex > 0}
    >
            {currentStep && (() => {
              const stepKey = stepKeyOf(currentStep);
              const commentRequired = isStepCommentRequired(currentStep);
              // Show the comment box when required, when the reviewer changed any field
              // from the value shown on arrival (the override if pre-changed, else the
              // extraction — i.e. `merged`), or for non-core (optional-note) fields.
              // Comparing against `merged` (not `extracted`) is what makes the box appear
              // whenever the reviewer edits a pre-changed value, even back to the extraction.
              const valueEdited = currentStep.fields.some(
                (f) => !fieldValuesEqual(editedValues[f] ?? merged[f], merged[f]),
              );
              const showComment =
                commentRequired || valueEdited || !CORE_REQUIRED_FIELDS.includes(stepKey);
              return (
                <ValidationStepEditor
                  key={stepKey}
                  fields={currentStep.fields}
                  companionFields={currentStep.fields.filter(
                    (f) => f !== stepKey && fieldNeedsInput(f, merged),
                  )}
                  values={editedValues}
                  onChange={handleChange}
                  showComment={showComment}
                  preChanged={isStepPreChanged(currentStep)}
                  editing={!!editing[stepKey]}
                  onEditToggle={() => handleEditToggle(stepKey)}
                  isAccurate={accurate[stepKey] !== false}
                  onAccurateChange={(acc) => handleAccurateChange(stepKey, acc)}
                  comment={comments[stepKey] || ''}
                  commentRequired={commentRequired}
                  onCommentChange={(c) => handleCommentChange(stepKey, c)}
                />
              );
            })()}
    </StepReviewModal>
  );
};

export default ValidateSessionModal;
